import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { contentApi } from "../api";
import TileCard from "./components/TileCard";
import type { CSSProperties } from "react";

export default function DepartmentPage() {
  const { slug } = useParams<{ slug: string }>();
  const { data, isLoading } = useQuery({ queryKey: ["content"], queryFn: contentApi.get });

  if (isLoading) {
    return <div className="max-w-4xl mx-auto px-8 py-12 text-ink-500 text-[13px]">Loading…</div>;
  }
  if (!data) return null;

  const dept = data.departments.find((d) => d.slug === slug);
  if (!dept) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-12">
        <p className="text-ink-500 text-[13px]">Department not found.</p>
        <Link to="/" className="btn-secondary mt-4">Back home</Link>
      </div>
    );
  }

  const tileCount = dept.sections.reduce((acc, s) => acc + s.tiles.length, 0);
  const accentStyle: CSSProperties = {
    ["--accent-color" as never]: dept.color,
  };

  return (
    <div className="max-w-4xl mx-auto px-8 py-10">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-[12.5px] text-ink-500 hover:text-ink-900 mb-8 transition-colors"
      >
        <ChevronLeft className="size-3.5" /> All departments
      </Link>

      <header className="mb-10 pb-8 border-b border-ink-200/70">
        <div className="flex items-center gap-2 mb-3">
          <span className="dot" style={{ backgroundColor: dept.color }} aria-hidden />
          <span className="eyebrow">Department</span>
        </div>
        <h1 className="display text-[44px] sm:text-[52px] leading-[1.05] tracking-tight font-medium text-ink-900">
          {dept.name}
        </h1>
        {dept.description && (
          <p className="text-[15px] text-ink-500 mt-3 max-w-2xl leading-[1.55]">{dept.description}</p>
        )}
        <div className="mt-5 flex items-center gap-3 text-[12px] text-ink-400">
          <span>
            <span className="display italic">{dept.sections.length}</span>{" "}
            {dept.sections.length === 1 ? "section" : "sections"}
          </span>
          <span className="text-ink-300">·</span>
          <span>
            <span className="display italic">{tileCount}</span>{" "}
            {tileCount === 1 ? "service" : "services"}
          </span>
        </div>
      </header>

      <div className="space-y-12">
        {dept.sections.map((s) => (
          <section key={s.id}>
            <div className="mb-4 flex items-baseline justify-between">
              <h2 className="display text-[22px] font-medium tracking-tight text-ink-900">
                {s.name}
              </h2>
              <span className="text-[11.5px] text-ink-400 font-mono">
                {String(s.tiles.length).padStart(2, "0")}
              </span>
            </div>
            <div
              style={accentStyle}
              className="surface accent-top divide-y divide-ink-100 overflow-hidden"
            >
              {s.tiles.map((t) => (
                <TileCard key={t.id} tile={t} variant="row" deptColor={dept.color} />
              ))}
              {s.tiles.length === 0 && (
                <div className="px-5 py-8 text-center text-[12.5px] text-ink-400">
                  No services in this section yet.
                </div>
              )}
            </div>
          </section>
        ))}

        {dept.sections.length === 0 && (
          <div className="surface p-12 text-center text-[13px] text-ink-500">
            No sections in this department yet.
          </div>
        )}
      </div>
    </div>
  );
}
